import { registerApplication, start } from 'single-spa';
import { constructApplications, constructRoutes, constructLayoutEngine } from 'single-spa-layout';
import './style.scss';
import { microfrontendApplicationsLoadService } from './services/microfrontendApplicationsLoadService';
import RenderingPlaceholders from './services/renderingPlaceholders';
import { addSideNavIsExpandEventListener } from './utils/addSideNavIsExpandEventListener';
import GraphQLService from './services/graphql-service';
import { getUserProfile } from './graphql/queries';
import { Amplify } from 'aws-amplify';
import { CONNECTION_STATE_CHANGE } from 'aws-amplify/api';
import { Hub } from 'aws-amplify/utils';
import { logoutService } from './services/logoutService';
import { registerInterceptorsService } from './services/registerInterceptorsService';
import { validUserLoggedInAuthService } from './services/validUserLoggedInAuthService';
import { unAuthorizedLayout } from './layouts';
import { shouldLoadMFE } from './utils/mfeJobCodeValidation';

const MFE_CONFIG_KEY = '@MFEConfig';

// Function to generate dynamic layout based on MFE config
const generateDynamicLayout = async (userRole, mfeConfig, userJobProfileId) => {
  let rootApps = '';
  let dynamicRoutes = '';

  if (mfeConfig?.MFES) {
    Object.values(mfeConfig.MFES).forEach(mfe => {
      if (
        mfe.enabled &&
        mfe.name &&
        mfe.type === 'app' &&
        mfe.route &&
        shouldLoadMFE(mfe, userRole, userJobProfileId)
      ) {
        if (mfe.route === '/') {
          rootApps += `
            <application name="@${mfe.name}-route" props="userProfile"></application>`;
        } else {
          dynamicRoutes += `
          <route path="${mfe.route}">
            <application name="@${mfe.name}-route" props="userProfile"></application>
          </route>`;
        }
      }
    });
  }

  return `<single-spa-router>
        <div class="globalnav">
          <application name="@swa-wfn-global-nav-mfe" props="userProfile,mfeConfig"></application>
        </div>
        <div id="main" class="router-outlet">
          <div id="authentication-error-screen"></div>
          <div id="unauthorized-screen"></div>
          <route path="/">${rootApps}
          </route>${dynamicRoutes}
        </div>
      </single-spa-router>`;
};

// Helper function to fetch user profile and determine layout
const fetchUserProfile = async () => {
  try {
    const userProfile = await GraphQLService.GraphQLQueryRequest(getUserProfile, {});
    const mfeConfigResponse = await window[MFE_CONFIG_KEY];
    const mfeConfig = mfeConfigResponse;
    const data = {
      props: {
        userProfile: userProfile?.data?.getUserProfile,
        mfeConfig
      }
    };
    const userRole = userProfile?.data?.getUserProfile?.userApplicationRole;
    const userJobProfileId = userProfile?.data?.getUserProfile?.jobProfileId;
    const layout = await generateDynamicLayout(userRole, mfeConfig, userJobProfileId);
    return { data, layout };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    const mfeConfigResponse = await window[MFE_CONFIG_KEY];
    const mfeConfig = mfeConfigResponse;
    const layout = await generateDynamicLayout(null, mfeConfig, null);
    return { data: null, layout };
  }
};

// Main function to initialize the application
export const initializeApp = async () => {
  const Auth = await window['@swa-wfn-auth-mfe'];
  const authLoginObject = await Auth?.Login();
  const isUserLoggedIn = authLoginObject?.userLoggedIn;
  const isAuthenticationError = authLoginObject?.authenticationError;
  const isAuthorized = authLoginObject?.isAuthorized;

  if (Auth) {
    logoutService(Auth);
    registerInterceptorsService(isUserLoggedIn);

    let data;
    let layout = unAuthorizedLayout;

    if (validUserLoggedInAuthService(isUserLoggedIn, isAuthorized)) {
      Amplify.configure({
        API: {
          GraphQL: {
            endpoint: process.env.REACT_APP_GRAPHQL_ENDPOINT,
            region: process.env.REACT_APP_GRAPHQL_REGION,
            defaultAuthMode: process.env.REACT_APP_GRAPHQL_DEFAULT_AUTH_MODE
          }
        }
      });

      Hub.listen('api', hubData => {
        const { payload } = hubData;
        if (payload.event === CONNECTION_STATE_CHANGE) {
          const connectionState = payload.data.connectionState;
          console.log(connectionState);
        }
      });

      const profileResult = await fetchUserProfile();
      data = profileResult.data;
      layout = profileResult.layout;
    }

    // Construct routes from the layout element and the provided data
    const routes = constructRoutes(layout, data);

    // Construct applications based on the defined routes
    const applications = constructApplications({
      routes,
      loadApp: async ({ name }) =>
        await microfrontendApplicationsLoadService(name, data?.props?.userProfile)
    });

    // Create a layout engine to manage the applications and routes
    const layoutEngine = constructLayoutEngine({ routes, applications });

    // Activate the layout engine to start managing the application layout
    layoutEngine.activate();

    // Register each application with single-spa
    applications.forEach(registerApplication);

    // Start the single-spa framework
    start();

    let unauthorizedScreenRoot = null;
    let authenticationErrorElementRoot = null;
    window.addEventListener('single-spa:routing-event', () => {
      unauthorizedScreenRoot = RenderingPlaceholders.renderUnauthorizedScreen(
        unauthorizedScreenRoot,
        isUserLoggedIn,
        isAuthorized
      );
      authenticationErrorElementRoot = RenderingPlaceholders.renderAuthenticationErrorScreen(
        authenticationErrorElementRoot,
        isAuthenticationError
      );
    });
    addSideNavIsExpandEventListener();
  }
};

initializeApp();
